import axios from "axios";
import BASE_URL from "../../config";

const getAuthHeader = () => {
  const tokenData = JSON.parse(localStorage.getItem("authUser") || "{}");
  return {
    Authorization: `Bearer ${tokenData.accessToken}`,
    'Content-Type': 'application/json'
  };
};

export const getPurchaseOrders = async (filter = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/PurchaseOrder/get`, filter, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching purchase orders:", error);
    throw error;
  }
};

// Helper function to clean identity column ID fields from payload
const cleanIdentityColumns = (data) => {
  if (!data || typeof data !== 'object') return data;
  
  // Create a deep copy to avoid mutating the original
  const cleaned = Array.isArray(data) ? [...data] : { ...data };
  
  // List of identity column patterns to clean
  const identityColumnPatterns = [
    'PurchaseOrder_Id',
    'PurchaseOrderDet_Id',
    'PurchaseOrderTaxDet_Id',
    'PurchaseStock_Id',
    'PurchaseOrderTaxDetails_Id', // Alternative naming
    'purchaseOrderTaxDet_Id' // Lowercase variant
  ];
  
  // Clean top-level identity columns
  identityColumnPatterns.forEach(pattern => {
    if (cleaned[pattern] !== undefined && (cleaned[pattern] === 0 || cleaned[pattern] === null)) {
      delete cleaned[pattern];
    }
  });
  
  // Recursively clean nested objects (Details, TaxDetails, StockDetails)
  if (cleaned.Details && Array.isArray(cleaned.Details)) {
    cleaned.Details = cleaned.Details.map(detail => {
      const cleanedDetail = { ...detail };
      identityColumnPatterns.forEach(pattern => {
        if (cleanedDetail[pattern] !== undefined && (cleanedDetail[pattern] === 0 || cleanedDetail[pattern] === null)) {
          delete cleanedDetail[pattern];
        }
      });
      return cleanedDetail;
    });
  }
  
  if (cleaned.TaxDetails && Array.isArray(cleaned.TaxDetails)) {
    cleaned.TaxDetails = cleaned.TaxDetails.map(tax => {
      const cleanedTax = { ...tax };
      if (cleanedTax.PurchaseOrderTaxDet_Id !== undefined && (cleanedTax.PurchaseOrderTaxDet_Id === 0 || cleanedTax.PurchaseOrderTaxDet_Id === null)) {
        delete cleanedTax.PurchaseOrderTaxDet_Id;
      }
      return cleanedTax;
    });
  }
  
  if (cleaned.StockDetails && Array.isArray(cleaned.StockDetails)) {
    cleaned.StockDetails = cleaned.StockDetails.map(stock => {
      const cleanedStock = { ...stock };
      if (cleanedStock.PurchaseStock_Id !== undefined && (cleanedStock.PurchaseStock_Id === 0 || cleanedStock.PurchaseStock_Id === null)) {
        delete cleanedStock.PurchaseStock_Id;
      }
      return cleanedStock;
    });
  }
  
  return cleaned;
};

export const savePurchaseOrder = async (purchaseOrder) => {
  try {
    // Clean identity column ID fields before sending to backend
    const cleanedPayload = cleanIdentityColumns(purchaseOrder);
    
    const response = await axios.post(`${BASE_URL}/api/PurchaseOrder/save`, cleanedPayload, {
      headers: getAuthHeader(),
      timeout: 120000,
    });
    return response.data;
  } catch (error) {
    // Handle validation errors (400 BadRequest) from backend
    if (error.response && error.response.status === 400 && error.response.data) {
      // Return the error response data so frontend can display the validation message
      return error.response.data;
    }
    // For other errors, throw to be caught by the calling function
    console.error("Error saving purchase order:", error);
    throw error;
  }
};

export const getNextInvoiceNo = async (businessId, isReturn = false, returnOriginalInvoiceNo = null) => {
  try {
    const payload = {
      Business_Id: businessId,
      IsReturn: isReturn
    };
    
    if (isReturn && returnOriginalInvoiceNo) {
      payload.Return_Original_InvoiceNo = returnOriginalInvoiceNo;
    }
    
    const response = await axios.post(`${BASE_URL}/api/PurchaseOrder/get-next-invoice-no`, payload, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error getting next invoice number:", error);
    throw error;
  }
};

// NEW: Get a single purchase return by id (read-only view)
export const getPurchaseReturnById = async (purchaseReturnId) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/PurchaseOrder/get-return-by-id`, {
      PurchaseReturn_Id: purchaseReturnId
    }, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error getting purchase return by id:", error);
    throw error;
  }
};

export const getPurchaseOrderForReturn = async (purchaseOrderId) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/PurchaseOrder/get-purchase-order-for-return`, {
      PurchaseOrder_Id: purchaseOrderId
    }, {
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching purchase order for return:", error);
    throw error;
  }
};

// API to export purchase summary to Excel
export const exportPurchaseSummaryToExcel = async (filter = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/PurchaseOrder/export-purchase-summary-excel`, filter, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const fromDateStr = filter.From_Date ? new Date(filter.From_Date).toISOString().slice(0,10).replace(/-/g, '') : 'All';
    const toDateStr = filter.To_Date ? new Date(filter.To_Date).toISOString().slice(0,10).replace(/-/g, '') : 'All';
    link.setAttribute('download', `PurchaseSummary_${fromDateStr}_to_${toDateStr}_${new Date().toISOString().slice(0,19).replace(/[-:]/g, '').replace('T', '_')}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error("Error exporting purchase summary:", error);
    throw error;
  }
};

// API to export purchase details to Excel
export const exportPurchaseDetailsToExcel = async (filter = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/PurchaseOrder/export-purchase-details-excel`, filter, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const fromDateStr = filter.From_Date ? new Date(filter.From_Date).toISOString().slice(0,10).replace(/-/g, '') : 'All';
    const toDateStr = filter.To_Date ? new Date(filter.To_Date).toISOString().slice(0,10).replace(/-/g, '') : 'All';
    link.setAttribute('download', `PurchaseDetails_${fromDateStr}_to_${toDateStr}_${new Date().toISOString().slice(0,19).replace(/[-:]/g, '').replace('T', '_')}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error("Error exporting purchase details:", error);
    throw error;
  }
};// API to export purchase return summary to Excel
export const exportPurchaseReturnSummaryToExcel = async (filter = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/PurchaseOrder/export-purchase-return-summary-excel`, filter, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const fromDateStr = filter.From_Date ? new Date(filter.From_Date).toISOString().slice(0,10).replace(/-/g, '') : 'All';
    const toDateStr = filter.To_Date ? new Date(filter.To_Date).toISOString().slice(0,10).replace(/-/g, '') : 'All';
    link.setAttribute('download', `PurchaseReturnSummary_${fromDateStr}_to_${toDateStr}_${new Date().toISOString().slice(0,19).replace(/[-:]/g, '').replace('T', '_')}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error("Error exporting purchase return summary:", error);
    throw error;
  }
};// API to export purchase return details to Excel
export const exportPurchaseReturnDetailsToExcel = async (filter = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/PurchaseOrder/export-purchase-return-details-excel`, filter, {
      headers: getAuthHeader(),
      responseType: 'blob'
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const fromDateStr = filter.From_Date ? new Date(filter.From_Date).toISOString().slice(0,10).replace(/-/g, '') : 'All';
    const toDateStr = filter.To_Date ? new Date(filter.To_Date).toISOString().slice(0,10).replace(/-/g, '') : 'All';
    link.setAttribute('download', `PurchaseReturnDetails_${fromDateStr}_to_${toDateStr}_${new Date().toISOString().slice(0,19).replace(/[-:]/g, '').replace('T', '_')}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error("Error exporting purchase return details:", error);
    throw error;
  }
};

// API to get purchase report summary
export const getPurchaseReportSummary = async (filter = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/PurchaseOrder/get-purchase-report-summary`, filter, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching purchase report summary:", error);
    throw error;
  }
};

// API to get purchase report details
export const getPurchaseReportDetails = async (filter = {}) => {
  try {
    const response = await axios.post(`${BASE_URL}/api/PurchaseOrder/get-purchase-report-details`, filter, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching purchase report details:", error);
    throw error;
  }
};